/**
 * Intelligent prefetch manager for route-based code splitting
 * Preloads chunks based on user navigation patterns
 */

interface PrefetchConfig {
  priority: 'high' | 'medium' | 'low';
  delay?: number;
  condition?: () => boolean;
}

class PrefetchManager {
  private prefetchQueue: Map<string, PrefetchConfig> = new Map();
  private prefetchedChunks: Set<string> = new Set();

  /**
   * Define prefetch rules based on current route
   */
  private prefetchRules: Record<string, Record<string, PrefetchConfig>> = {
    '/': {
      // From homepage, the user is likely to visit analytics next.
      'analytics-overview': { priority: 'high', delay: 2000 },
    },
    '/insights/overview': {
      'analytics-charts': { priority: 'high', delay: 500 },
    },
  };

  /**
   * Start prefetching based on current route
   */
  startPrefetch(currentPath: string): void {
    const rules = this.prefetchRules[currentPath];
    if (!rules) return;

    Object.entries(rules).forEach(([chunkName, config]) => {
      if (this.prefetchedChunks.has(chunkName)) return;

      // Check condition if specified
      if (config.condition && !config.condition()) return;

      // Schedule prefetch based on priority and delay
      const delay = config.delay || this.getDelayByPriority(config.priority);

      setTimeout(() => {
        this.prefetchChunk(chunkName);
      }, delay);
    });
  }

  /**
   * Prefetch a specific chunk - Vercel-safe without webpack comments
   */
  private async prefetchChunk(chunkName: string): Promise<void> {
    if (this.prefetchedChunks.has(chunkName)) return;
    if (import.meta.env.DEV) return;

    try {
      switch (chunkName) {
        case 'analytics-overview':
          await import('@/pages/analytics/InsightsPage');
          break;
        case 'analytics-charts':
          await import('@/components/analytics/viz/AdaptiveTimeSeries');
          break;
      }

      this.prefetchedChunks.add(chunkName);
    } catch {
      // Prefetch failures are non-critical; navigation will load the chunk on demand
    }
  }

  /**
   * Get delay based on priority
   */
  private getDelayByPriority(priority: 'high' | 'medium' | 'low'): number {
    switch (priority) {
      case 'high':
        return 1000;
      case 'medium':
        return 3000;
      case 'low':
        return 5000;
    }
  }

  /**
   * Clear prefetch queue and cache
   */
  clear(): void {
    this.prefetchQueue.clear();
    this.prefetchedChunks.clear();
  }
}

// Export singleton instance
export const prefetchManager = new PrefetchManager();

// Hook for React components
export function usePrefetch() {
  const startPrefetch = (path: string) => {
    // Only prefetch on desktop with good connection
    if ('connection' in navigator) {
      const connection = (navigator as any).connection;
      if (connection.effectiveType === '3g' || connection.effectiveType === '2g') {
        return; // Skip prefetch on slow connections
      }
    }

    // Use requestIdleCallback for better performance
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(() => {
        prefetchManager.startPrefetch(path);
      });
    } else {
      setTimeout(() => {
        prefetchManager.startPrefetch(path);
      }, 1000);
    }
  };

  return { startPrefetch };
}
